import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { projects, projectKinds, Project } from '../content/portfolio';
import Reveal from './Reveal';

interface RepoStats {
  stars: number;
  pushedAt: string;
}

/** Live stats from the public GitHub REST API; rows render fine without them. */
const useRepoStats = (items: Project[]): Record<string, RepoStats> => {
  const [stats, setStats] = useState<Record<string, RepoStats>>({});

  useEffect(() => {
    let cancelled = false;
    items
      .filter((p) => p.repo)
      .forEach(async (p) => {
        try {
          const { data } = await axios.get(`https://api.github.com/repos/${p.repo}`, {
            headers: { Accept: 'application/vnd.github+json' },
          });
          if (!cancelled) {
            setStats((prev) => ({ ...prev, [p.slug]: { stars: data.stargazers_count, pushedAt: data.pushed_at } }));
          }
        } catch {
          // Rate limit or private repo: keep the row without stats.
        }
      });
    return () => {
      cancelled = true;
    };
  }, [items]);

  return stats;
};

const Projects: React.FC = () => {
  const { language } = useLanguage();
  const c = projects[language];
  const kinds = projectKinds[language];
  const stats = useRepoStats(c.items);
  const [active, setActive] = useState(0);
  const fmt = new Intl.DateTimeFormat(language === 'pt' ? 'pt-BR' : 'en-CA', { month: 'short', year: 'numeric' });

  const details = (p: Project) => {
    const s = stats[p.slug];
    return (
      <>
        <p className="text-xl leading-snug">{p.summary}</p>
        <p className="mt-4 text-code-comment">{p.stack.join(', ')}</p>
        <p className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-xl">
          {p.liveUrl && (
            <a href={p.liveUrl} target="_blank" rel="noopener noreferrer" className="underline underline-offset-4 hover:opacity-70">
              {c.live}
            </a>
          )}
          {p.repo && (
            <a href={`https://github.com/${p.repo}`} target="_blank" rel="noopener noreferrer" className="underline underline-offset-4 hover:opacity-70">
              {c.code}
            </a>
          )}
          {s && (
            <span className="text-code-comment">
              {s.stars} {language === 'pt' ? 'estrelas' : 'stars'}, {fmt.format(new Date(s.pushedAt))}
            </span>
          )}
        </p>
      </>
    );
  };

  return (
    <section id="projects" data-ink="#e8e6e1" data-field="#14120f" className="bg-code-surface py-24 text-code-text sm:py-32">
      <div className="container-responsive">
        <Reveal>
          <h2 className="font-serif text-4xl font-medium sm:text-5xl">{c.title}</h2>
        </Reveal>
        <Reveal delay={0.08}>
          <p className="mt-5 max-w-2xl text-xl leading-snug">{c.intro}</p>
        </Reveal>

        <div className="mt-16 grid gap-x-16 lg:grid-cols-[minmax(0,34rem)_1fr]">
          <ul>
            {c.items.map((p, i) => (
              <li key={p.slug} className="py-4">
                <button
                  type="button"
                  onMouseEnter={() => setActive(i)}
                  onFocus={() => setActive(i)}
                  onClick={() => setActive(i)}
                  aria-expanded={active === i}
                  className={`block w-full text-left transition-opacity duration-150 lg:opacity-100 ${
                    active === i ? 'lg:opacity-100' : 'lg:opacity-60 hover:lg:opacity-100'
                  }`}
                >
                  <span className="block font-serif text-3xl leading-tight">{p.name}</span>
                  <span className="mt-1 block text-base text-code-comment">{kinds[p.slug]}</span>
                </button>
                <div className="mt-4 lg:hidden">{details(p)}</div>
              </li>
            ))}
          </ul>

          <div className="hidden lg:block">
            <div className="sticky top-32 max-w-xl pt-4" aria-live="polite">
              <h3 className="font-serif text-3xl leading-tight">{c.items[active].name}</h3>
              <div className="mt-5">{details(c.items[active])}</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Projects;
